import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import BioPageRenderer from "../components/bio/BioPageRenderer";
import NotFound from "./NotFound";
import { openExternalUrl, normalizeExternalUrl } from "../lib/url-utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function PublicBioPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  // ── TWO FAILURES, NOT ONE ───────────────────────────────────────────
  // `notFound` means the backend ANSWERED and said this slug does not
  // exist. `loadError` means we could not get an answer. They used to be
  // one `error` string set from a bare `catch`, so a dropped connection
  // or a 5xx told a creator their live bio page did not exist — and the
  // only way back was a reload they had no reason to try.
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  //: Bumped by "Try again"; re-runs the fetch effect.
  const [attempt, setAttempt] = useState(0);

  // Email capture state
  const [leadEmail, setLeadEmail] = useState("");
  const [leadStatus, setLeadStatus] = useState(""); // "" | "sending" | "success" | "error"
  const [leadEmailError, setLeadEmailError] = useState("");

  useEffect(() => {
    // ── IGNORE A RESPONSE THAT IS NO LONGER WANTED ────────────────────
    // The slug can change while a request is in flight (a client-side
    // navigation from one bio page to another). Without this, the slow
    // response for the OLD slug resolves last and overwrites the new
    // slug's state — the visitor reads one creator's page under another
    // creator's URL, and on a 404 for the old slug they get the 404 for
    // a page that exists.
    let cancelled = false;

    (async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/bio/${slug}`);
        if (cancelled) return;
        // A 2xx carrying nothing is not a page. It is also not a 404:
        // the backend did not say this slug is missing, so treat it the
        // way we treat any other answer we cannot use.
        if (!response || !response.data) {
          setLoadError(true);
        } else {
          setPage(response.data);
        }
      } catch (err) {
        if (cancelled) return;
        // ONLY a confirmed 404 means "there is nothing here". Everything
        // else — offline, timeout, 5xx, a CORS failure with no response
        // at all — means we could not ask.
        if (err?.response?.status === 404) setNotFound(true);
        else setLoadError(true);
      }
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [slug, attempt]);

  /** Reset and re-run the same fetch. Shows the existing skeleton while
   *  it retries, so a retry looks like the first load. */
  const retry = useCallback(() => {
    setLoadError(false);
    setNotFound(false);
    setPage(null);
    setLoading(true);
    setAttempt((n) => n + 1);
  }, []);

  const trackClick = async (linkId, linkTitle) => {
    try {
      await axios.post(`${BACKEND_URL}/api/bio/${slug}/click-detailed`, { link_id: linkId, link_title: linkTitle || "" });
    } catch { /* no-op */ }
  };

  const handleGridItemClick = (item) => {
    trackClick(item.id, item.caption);
    if (item.link_url) openExternalUrl(item.link_url);
  };
  const handleButtonClick = (link) => {
    trackClick(link.id, link.title);
    if (link.url) openExternalUrl(link.url);
  };
  /** An Instagram post or reel tile.
   *
   *  Tracked through the SAME click ledger the links and grid tiles use,
   *  so embed taps land in the existing link-in-bio analytics rather than
   *  a parallel one nobody reads. The label is the URL because an embed
   *  has no title of its own. */
  const handleEmbedClick = (emb) => {
    trackClick(emb.id, emb.embed_url);
    if (emb.embed_url) openExternalUrl(emb.embed_url);
  };
  /** Buy now on a product card.
   *
   *  The renderer has always accepted `onProductClick`, and NOBODY passed
   *  it — so the button rendered, looked live, and did nothing on every
   *  shop page. Tracked through the SAME click ledger the link buttons
   *  use, so product clicks land in the existing analytics rather than a
   *  parallel one.
   */
  const handleProductClick = (product, link) => {
    trackClick(`product-${product?.id}`, product?.title || "Product");
    if (link?.url) openExternalUrl(link.url);
  };
  const handleSocialClick = (platform, url) => {
    trackClick(`social-${platform}`, platform);
    if (!url) return;
    if (platform === "whatsapp") {
      // Strip any non-numeric except leading + → wa.me/NUMBER
      const digits = url.replace(/[^0-9+]/g, "");
      openExternalUrl(`https://wa.me/${digits.replace(/^\+/, "")}`);
      return;
    }
    if (platform === "email") {
      openExternalUrl(`mailto:${url}`);
      return;
    }
    openExternalUrl(url);
  };

  const submitLead = async () => {
    if (!leadEmail || !page?.id) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadEmail)) {
      setLeadEmailError("Please enter a valid email address");
      return;
    }
    setLeadEmailError("");
    setLeadStatus("sending");
    try {
      await axios.post(`${BACKEND_URL}/api/bio/lead`, { page_id: page.id, email: leadEmail });
      setLeadStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) setLeadEmailError("Too many requests. Please wait a moment.");
      else if (err.response?.status === 400) setLeadEmailError(detail || "Invalid email address");
      else setLeadEmailError("Something went wrong. Please try again.");
      setLeadStatus("error");
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-pulse text-center">
        <div className="w-24 h-24 rounded-full bg-gray-200 mx-auto mb-4" />
        <div className="w-32 h-4 bg-gray-200 rounded mx-auto mb-2" />
        <div className="w-24 h-3 bg-gray-200 rounded mx-auto" />
      </div>
    </div>
  );

  // The backend confirmed this slug does not exist.
  if (notFound) return <NotFound />;

  // We could not get an answer. NOT the 404 — see the state declarations.
  if (loadError || !page) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Couldn't load this page.</h1>
        <p className="text-gray-500 mb-6">Check your connection and try again.</p>
        <button
          onClick={retry}
          data-testid="bio-retry"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );

  return (
    <BioPageRenderer
      page={page}
      isPreview={false}
      leadState={{
        email: leadEmail,
        status: leadStatus,
        error: leadEmailError,
        onChange: (v) => { setLeadEmail(v); setLeadEmailError(""); },
        onSubmit: submitLead,
      }}
      handlers={{
        onSocialClick: handleSocialClick,
        onButtonClick: handleButtonClick,
        onGridClick: handleGridItemClick,
        onProductClick: handleProductClick,
        onEmbedClick: handleEmbedClick,
      }}
    />
  );
}
